/* eslint-disable @atlaskit/editor/no-re-export */
// Entry file in package.json

import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { Transformer } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import type { Schema, Node as PMNode } from '@atlaskit/editor-prosemirror/model';

// Ignored via go/ees005
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createEncoder<T>(parser: Transformer<T>, encoder: Transformer<any>) {
	return (value: T) => encoder.encode(parser.parse(value));
}
export type TransformerProvider<T> = (schema: Schema) => Transformer<T>;
export class ADFEncoder<T> {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	encode: (value: T) => any;

	constructor(createTransformerWithSchema: TransformerProvider<T>) {
		const transformer = createTransformerWithSchema(defaultSchema);
		this.encode = createEncoder(transformer, new JSONTransformer());
	}
}

export const getText = (node: PMNode | ADNode): string => {
	return (
		node.text ||
		(node.attrs && (node.attrs.text || node.attrs.shortName)) ||
		`[${typeof node.type === 'string' ? node.type : node.type.name}]`
	);
};

export const getEventHandler = (
	eventHandlers?: EventHandlers,
	type?: keyof EventHandlers,
	eventName: string = 'onClick',
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return eventHandlers && type && eventHandlers[type] && (eventHandlers as any)[type][eventName];
};

/**
 * Traverse through parent elements of element. Return element for which evaluate(element) returns
 * true. If topElement is reached before evaluate returns true, return false. Does not run evaluate
 * on topElement.
 * @param element Starting HTMLElement
 * @param topElement HTMLElement to end search at. evaluate is not called on this element
 * @param evaluate Function which returns true or false based on the given element. eg: Checks if
 * element has desired classname.
 */

export function findInTree(
	element: HTMLElement,
	topElement: HTMLElement,
	evaluate: (element: HTMLElement) => boolean,
): boolean {
	if (element === topElement) {
		return false;
	}
	if (evaluate(element)) {
		return true;
	}
	if (!element.parentElement) {
		return false;
	}
	return findInTree(element.parentElement, topElement, evaluate);
}
